import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';

import { AboutData, SiteApiService } from '../../core/services/api.service';

declare var L: any;

@Component({
    selector: 'app-about',
    templateUrl: './about.component.html'
})
export class AboutComponent implements OnInit, AfterViewInit, OnDestroy {

    data: AboutData = null as any;
    loading = false;
    errorMessage = '';
    videoStarted = false;

    private observer: IntersectionObserver | null = null;
    private mouseMoveHandler: any;
    private map: any = null;

    constructor(
        private siteApi: SiteApiService,
        private elementRef: ElementRef
    ) { }

    ngOnInit(): void {
        this.getAbout();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initRevealAnimation();
            this.initAboutVisualMotion();
            this.initOfficeMap();
        }, 400);
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }

        const visual = this.elementRef.nativeElement.querySelector('.about-premium-visual');

        if (visual && this.mouseMoveHandler) {
            visual.removeEventListener('mousemove', this.mouseMoveHandler);
        }

        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    getAbout(): void {
        this.loading = true;
        this.errorMessage = '';

        this.siteApi.getAbout().subscribe(
            (res: AboutData) => {
                this.data = res;
                this.loading = false;

                setTimeout(() => {
                    this.initRevealAnimation();
                    this.initAboutVisualMotion();
                    this.initOfficeMap();
                }, 300);
            },
            (error) => {
                console.error(error);
                this.errorMessage = 'About page data could not be loaded.';
                this.loading = false;
            }
        );
    }

    playAboutVideo(video: HTMLVideoElement): void {
        this.videoStarted = true;
        video.play();
    }

    private initRevealAnimation(): void {
        const elements = this.elementRef.nativeElement.querySelectorAll('.reveal');

        if (!elements || elements.length === 0) {
            return;
        }

        if (this.observer) {
            this.observer.disconnect();
        }

        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const el = entry.target as HTMLElement;
                        el.classList.add('show');
                        this.observer?.unobserve(el);
                    }
                });
            },
            {
                threshold: 0.14
            }
        );

        elements.forEach((el: HTMLElement) => {
            el.classList.remove('show');
            this.observer?.observe(el);
        });
    }

    private initAboutVisualMotion(): void {
        const visual = this.elementRef.nativeElement.querySelector('.about-premium-visual') as HTMLElement;
        const inner = this.elementRef.nativeElement.querySelector('.about-premium-visual-inner') as HTMLElement;

        if (!visual || !inner) {
            return;
        }

        this.mouseMoveHandler = (event: MouseEvent) => {
            const rect = visual.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width - 0.5;
            const y = (event.clientY - rect.top) / rect.height - 0.5;

            inner.style.transform = `translate3d(${x * 16}px, ${y * 14}px, 0) scale(1.03)`;
        };

        visual.addEventListener('mousemove', this.mouseMoveHandler);

        visual.addEventListener('mouseleave', () => {
            inner.style.transform = 'translate3d(0,0,0) scale(1)';
        });
    }

    private initOfficeMap(): void {
        const mapElement = document.getElementById('ioscOfficeMap');

        if (!mapElement || typeof L === 'undefined') {
            return;
        }

        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        this.map = L.map('ioscOfficeMap', {
            scrollWheelZoom: false
        }).setView([42.5, 45.5], 4);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(this.map);

        const offices = [
            {
                title: 'United Kingdom Office',
                lat: 50.967,
                lng: -1.352,
                html:
                    '<b>United Kingdom</b><br>' +
                    'Suit 9, Woodside House, Woodside Avenue, Eastleigh, SO50 9NA United Kingdom<br>' +
                    'Tel.: +44(0) 23 8065 0900<br>' +
                    'Fax: +44(0) 23 8064 2556<br>' +
                    'E-mail: sales@iosc.co.uk'
            },
            {
                title: 'Qatar Office',
                lat: 25.2684924,
                lng: 51.5228928,
                html:
                    '<b>Qatar</b><br>' +
                    'Zone 24, Street 220, Building 149 Doha, Qatar<br>' +
                    'Tel.: +974 6640 7679 / +974 5207 1774<br>' +
                    'E-mail: jasadov@iosc.qa / rrahimov@iosc.qa'
            },
            {
                title: 'Azerbaijan Office',
                lat: 40.235,
                lng: 49.556,
                html:
                    '<b>Azerbaijan</b><br>' +
                    'Salyan Highway, 32 km, Garadagh district, AZ1083<br>' +
                    'Mob.: +99 (450) 22420 57 / +99 (450) 2242 58<br>' +
                    'E-mail: office@iosc.az / sales@iosc.az'
            },
            {
                title: 'Georgia Office',
                lat: 41.725,
                lng: 44.745,
                html:
                    '<b>Georgia</b><br>' +
                    'Vake-saburtalo district, Sairme Hill N 31 Tbilisi Georgia<br>' +
                    'Tel.: +99 (559) 1069761 / +99 (450) 2242057<br>' +
                    'E-mail: ali.rajabov@iosc.ge'
            },
            {
                title: 'Kazakhstan Office',
                lat: 43.238,
                lng: 76.945,
                html:
                    '<b>Kazakhstan</b><br>' +
                    '498 Seifullin str. BC Altyn Zaman office 305 Almaty 050012 Kazakhstan<br>' +
                    'Tel./Fax: +7 (727) 9738470<br>' +
                    'Mob.: +77750000220<br>' +
                    'E-mail: office@ioscca.kz'
            }
        ];

        const markers: any[] = [];

        offices.forEach(o => {
            const marker = L.marker([o.lat, o.lng]).addTo(this.map);
            marker.bindPopup(o.html);
            markers.push(marker);
        });

        const group = L.featureGroup(markers);
        this.map.fitBounds(group.getBounds().pad(0.22));

        setTimeout(() => {
            this.map.invalidateSize();
        }, 300);
    }
}
